import { Controller } from "@hotwired/stimulus";
import Sortable from "sortablejs";
import { Collapse } from "bootstrap";
import { generate } from "../games/math_crossword/generator.js";

const SETTINGS_STORAGE_KEY = "math-crossword:settings:v1";

export default class extends Controller {
  static targets = ["settings", "grid", "pool", "status"];

  connect() {
    this.sortables = [];
    this.restoreSettings();
    this.newGameFromSettings();
  }

  disconnect() {
    this.destroySortables();
  }

  newGame(event) {
    if (event) event.preventDefault();
    this.newGameFromSettings();
    this.collapseSettings();
  }

  collapseSettings() {
    const panel = document.getElementById("mc-settings");
    if (panel?.classList.contains("show")) Collapse.getOrCreateInstance(panel).hide();
  }

  newGameFromSettings() {
    const opts = this.readSettings();
    let puzzle;
    try {
      puzzle = generate(opts);
    } catch (e) {
      console.error("math-crossword: generate failed", e);
      return;
    }
    this.puzzle = puzzle;
    this.render(puzzle);
    this.wireSortable();
    this.clearStatus();
  }

  reset() {
    if (!this.puzzle) return;
    const poolEl = this.poolTarget;
    for (const cell of this.blankCells()) {
      while (cell.firstChild) poolEl.appendChild(cell.firstChild);
    }
    this.sortPool();
    this.clearStatus();
    this.clearInvalid();
  }

  solve() {
    if (!this.puzzle) return;
    this.reset();
    for (const blank of this.blankCells()) {
      const answer = blank.dataset.answer;
      const item = [...this.poolTarget.querySelectorAll(".mc-pool-item")]
        .find((i) => i.dataset.value === answer);
      if (item) blank.appendChild(item);
    }
    this.clearInvalid();
    this.showStatus("info", "Here's the solution.");
    this.poolTarget.classList.add("d-none");
  }

  sortPool() {
    const pool = this.poolTarget;
    const items = [...pool.children];
    items.sort((a, b) => Number(a.dataset.value) - Number(b.dataset.value));
    for (const item of items) pool.appendChild(item);
  }

  readSettings() {
    const base = { maxCols: this.viewportMaxCols() };
    if (!this.hasSettingsTarget) return base;
    const form = this.settingsTarget;
    const ops = [...form.querySelectorAll("input[name='ops']:checked")].map((i) => i.value);
    const sizeInput = form.querySelector("input[name='size']:checked");
    const size = sizeInput ? sizeInput.value : "medium";
    this.persistSettings({ ops, size });
    return { ...base, ops, size };
  }

  restoreSettings() {
    if (!this.hasSettingsTarget) return;
    const saved = this.loadPersistedSettings();
    if (!saved) return;
    const form = this.settingsTarget;
    if (Array.isArray(saved.ops)) {
      for (const box of form.querySelectorAll("input[name='ops']")) {
        box.checked = saved.ops.includes(box.value);
      }
      const anyChecked = form.querySelector("input[name='ops']:checked");
      if (!anyChecked) {
        const first = form.querySelector("input[name='ops']");
        if (first) first.checked = true;
      }
    }
    if (typeof saved.size === "string") {
      const radio = form.querySelector(`input[name='size'][value='${saved.size}']`);
      if (radio) radio.checked = true;
    }
  }

  loadPersistedSettings() {
    try {
      const raw = window.localStorage?.getItem(SETTINGS_STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  persistSettings(settings) {
    try {
      window.localStorage?.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settings));
    } catch {
      // storage unavailable; skip silently
    }
  }

  viewportMaxCols() {
    if (typeof window === "undefined") return undefined;
    // Mirror the CSS cell sizing (clamp(2rem, 11vw, 3.25rem) + 0.2rem gap) so
    // the generated grid actually fits the viewport without horizontal overflow.
    const vw = window.innerWidth;
    const gutter = 32;
    const gap = 3.2;
    const cellPreferred = Math.min(52, Math.max(32, vw * 0.11));
    return Math.max(5, Math.floor((vw - gutter + gap) / (cellPreferred + gap)));
  }

  render(puzzle) {
    this.element.style.setProperty("--mc-cols", String(puzzle.cols));
    const grid = this.gridTarget;
    grid.innerHTML = "";

    for (let r = 0; r < puzzle.rows; r++) {
      for (let c = 0; c < puzzle.cols; c++) {
        const cell = puzzle.grid[r][c];
        grid.appendChild(this.buildCell(cell));
      }
    }

    const pool = this.poolTarget;
    pool.innerHTML = "";
    pool.classList.remove("d-none");
    for (const value of puzzle.pool) {
      pool.appendChild(this.buildPoolItem(value));
    }
  }

  buildCell(cell) {
    const el = document.createElement("div");
    if (cell === null) {
      el.className = "mc-cell mc-cell--empty";
      return el;
    }
    if (cell.type === "given") {
      el.className = "mc-cell mc-cell--given";
      el.textContent = cell.value;
      return el;
    }
    if (cell.type === "op") {
      el.className = "mc-cell mc-cell--operator";
      el.textContent = cell.value;
      return el;
    }
    // blank
    el.className = "mc-cell mc-cell--blank";
    el.dataset.answer = cell.answer;
    el.setAttribute("aria-label", "Blank cell");
    el.setAttribute("role", "group");
    return el;
  }

  buildPoolItem(value) {
    const el = document.createElement("div");
    el.className = "mc-pool-item";
    el.dataset.value = value;
    el.textContent = value;
    return el;
  }

  wireSortable() {
    this.destroySortables();
    const shared = {
      animation: 150,
      forceFallback: true,
      fallbackOnBody: true,
      ghostClass: "mc-drag-ghost",
      chosenClass: "mc-drag-chosen",
      onAdd: () => this.afterMove(),
      onRemove: () => this.afterMove(),
    };

    this.sortables.push(
      Sortable.create(this.poolTarget, {
        ...shared,
        group: { name: "mc", pull: true, put: true },
      }),
    );

    for (const cell of this.blankCells()) {
      this.sortables.push(
        Sortable.create(cell, {
          ...shared,
          group: {
            name: "mc",
            pull: true,
            put: (to) => {
              const el = to && (to.el || to);
              return el && el.children.length === 0;
            },
          },
        }),
      );
    }
  }

  destroySortables() {
    if (!this.sortables) return;
    for (const s of this.sortables) s.destroy();
    this.sortables = [];
  }

  blankCells() {
    return this.gridTarget.querySelectorAll(".mc-cell--blank");
  }

  afterMove() {
    this.clearInvalid();
    this.sortPool();
    const blanks = [...this.blankCells()];
    const allFilled = blanks.every((b) => b.firstChild);
    if (!allFilled) {
      this.clearStatus();
      return;
    }
    const wrong = blanks.filter((b) => b.firstChild.dataset.value !== b.dataset.answer);
    if (wrong.length === 0) {
      this.showStatus("success", "Correct! Nicely done.");
      this.poolTarget.classList.add("d-none");
    } else {
      for (const cell of wrong) cell.classList.add("mc-cell--invalid");
      this.showStatus("warning", `Not quite — ${wrong.length} cell${wrong.length === 1 ? "" : "s"} off.`);
    }
  }

  showStatus(kind, message) {
    if (!this.hasStatusTarget) return;
    const variant = { success: "success", warning: "warning", info: "info" }[kind] ?? "warning";
    const el = this.statusTarget;
    el.className = `mc-status alert alert-${variant}`;
    el.replaceChildren();
    const msg = document.createElement("span");
    msg.textContent = message;
    el.appendChild(msg);
    if (kind === "success") {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "btn btn-primary btn-sm ms-3";
      btn.textContent = "New Puzzle";
      btn.dataset.action = "click->math-crossword#newGame";
      el.appendChild(btn);
    }
  }

  clearStatus() {
    if (!this.hasStatusTarget) return;
    const el = this.statusTarget;
    el.className = "mc-status d-none";
    el.textContent = "";
    this.poolTarget.classList.remove("d-none");
  }

  clearInvalid() {
    for (const el of this.gridTarget.querySelectorAll(".mc-cell--invalid")) {
      el.classList.remove("mc-cell--invalid");
    }
  }
}
